import { z } from 'zod';
import { phoneOptionalSchema } from '../phones/index.js';
export const MeetingBodyType = {
    primary: 'primary',
    committee: 'committee',
    board: 'board',
};
const meetingBodyClerkSchema = z.object({
    id: z.number(),
    default: z.boolean(),
});
const normalizeClerks = (clerks) => {
    const defaultClerkId = clerks.find((clerk) => clerk.default)?.id;
    const uniqueIds = [...new Set(clerks.map(({ id }) => id))];
    return uniqueIds.map((id) => ({ id, default: id === defaultClerkId }));
};
export const meetingBodyClerksSchema = z.array(meetingBodyClerkSchema).transform(normalizeClerks);
// Base validation and DTO type
export const meetingBodyBaseSchema = z.object({
    name: z
        .string('Name is required')
        .min(1, 'Name is required')
        .max(300, 'Name must be less than 300 characters')
        .describe('Required for create'),
    type: z.enum(MeetingBodyType, 'Meeting body type is required').describe('Required for create'),
    adminId: z.number('Must assign an admin to the meeting body').describe('Required for create'),
    contactId: z.number('Must assign a contact to the meeting body').describe('Required for create'),
    overview: z.string().nullable().optional(),
    recurringInterval: z.string().nullable().optional(),
    recurringDay: z.string().nullable().optional(),
    recurringTime: z
        .string()
        .nullable()
        .optional()
        .refine((time) => {
        if (!time)
            return true;
        const [hours, minutes] = time.split(':').map(Number);
        return (typeof hours === 'number' &&
            typeof minutes === 'number' &&
            hours >= 0 &&
            hours < 24 &&
            minutes >= 0 &&
            minutes < 60);
    }, {
        message: 'Invalid time format. Please use HH:MM format.',
    }),
    location: z.string().nullish(),
    locationId: z.number().nullish(),
    virtualMeetingUrl: z.url('Must be a valid URL').nullish(),
    meetingDialIn: z.string().nullish(),
    meetingVirtualId: z.string().nullish(),
    contactName: z.string().nullable().optional(),
    contactEmail: z.email().nullable().optional(),
    contactPhone: phoneOptionalSchema.nullable().optional(),
    info: z.string().nullable().optional(),
    isActive: z.boolean().optional(),
    isPrivate: z.boolean().optional(),
    enableMemberVoting: z.boolean().optional(),
    autoPublishMediaArchive: z.boolean().optional(),
    logoUrl: z.url().nullable().optional(),
    website: z.url('Invalid website URL').nullable().optional(),
    meetingBodyCategoryIds: z.array(z.number()).optional(),
    agendaTemplates: z
        .array(z.object({
        agendaTemplateId: z.number(),
        isDefault: z.boolean(),
    }))
        .optional()
        .refine((templates) => {
        if (!templates || templates.length === 0) {
            return true; // Allow 0 templates
        }
        // If templates are selected, ensure exactly one is marked as default
        const defaultTemplates = templates.filter((template) => template.isDefault);
        return defaultTemplates.length === 1;
    }, {
        message: 'Exactly one agenda template must be set as the default template when templates are selected',
    }),
    seatApplicationTemplateId: z.number().nullish(),
    seatApplicationWorkflowId: z.number().nullish(),
    legislativeTypes: z
        .array(z.object({
        id: z.number(),
    }))
        .optional(),
    clerks: meetingBodyClerksSchema.optional(),
});
export const meetingBodyCreateSchema = meetingBodyBaseSchema.transform((data) => {
    // Set location to null if locationId has a valid value
    if (data.locationId !== null && data.locationId !== undefined) {
        return {
            ...data,
            location: null,
        };
    }
    return data;
});
export const meetingBodyBulkCreateSchema = z
    .array(meetingBodyCreateSchema)
    .min(1, 'Meeting bodies array is required and must not be empty');
// Update validation and DTO type
export const meetingBodyUpdateSchema = meetingBodyBaseSchema.partial().transform((data) => {
    // Set location to null if locationId has a valid value
    if (data.locationId !== null && data.locationId !== undefined) {
        return {
            ...data,
            location: null,
        };
    }
    return data;
});
/**
 * Request body for sending a free-form message to selected members of a meeting body
 * from the Board Roster. Recipients are identified by their meeting member IDs.
 */
export const sendBoardMessageSchema = z.object({
    subject: z
        .string('Subject is required')
        .trim()
        .min(1, 'Subject is required')
        .max(200, 'Subject is too long'),
    body: z
        .string('Message is required')
        .trim()
        .min(1, 'Message is required')
        .max(5000, 'Message is too long'),
    memberIds: z.array(z.number()).min(1, 'Select at least one member'),
    /**
     * When true, replies go to the sending government user instead of the shared notifications inbox.
     */
    replyToSender: z.boolean().default(true),
});
